import { useDocPermissions } from "@/components/access/use-doc-permissions";
import { useCollaborationStatus } from "@/features/collaboration/hooks/useCollaborationStatus";
import { useHocuspocusEnabled } from "@/features/collaboration/yjs/use-hocuspocus-enabled";
import { invertFlatData } from "@/shared";
import { useGetCurrentUserQuery } from "@/shared/services";
import { useUpdateVersionInDatabaseMutation } from "@api/versions";
import type { IVersion } from "@/types";
import type { StoryType } from "@config";
import { trackContent } from "@editor/utils";
import { type RootState, useAppDispatch, useAppSelector } from "@store";
import {
  handleAutosave,
  setIsSaving,
  setVersion,
  updateStoryByKeys,
} from "@store/story";
import {
  getEntities,
  getLastChangeId,
  getRestStory,
  getVersion,
  isAutoSaveEnabled,
  isLoadedDocument,
} from "@store/story/selectors";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useStore } from "react-redux";
import { toast } from "react-toastify";
import { useAutosaveAnalytics } from "./use-autosave-analytics";
import { useUpdatePreviewAndMeta } from "./use-update-preview-and-meta";
import { useUpdatePreviewOnly } from "./use-update-preview-only";

export const useAutosave = () => {
  "use no memo";
  const dispatch = useAppDispatch();
  const reduxStore = useStore<RootState>();
  const flatContentLayers = useAppSelector(getEntities);
  const isAutosave = useAppSelector(isAutoSaveEnabled);
  const story = useAppSelector(getRestStory);
  const version = useAppSelector(getVersion);
  const changeId = useAppSelector(getLastChangeId);
  // Start from the change id already in the store, not from null. The editor
  // route is keyed by storyId+versionId, so opening a version from its template
  // (or going back) remounts this hook over a store that still holds the
  // PREVIOUS document and its last change id; nothing resets that id on load.
  // Treating it as a pending edit saved that leftover document from the new
  // route — the version's content and name were uploaded as the template's
  // head (template 6ab224c4…, 2026-09-24). Only edits made after this mount
  // are this instance's to save; the previous instance's own debounce still
  // flushes anything it had queued.
  const lastChangeId = useRef<string | null>(changeId ?? null);
  const [updateVersionInDatabase] = useUpdateVersionInDatabaseMutation();
  const { data: me } = useGetCurrentUserQuery();
  const isCollaborative = useCollaborationStatus();
  const hocuspocusEnabled = useHocuspocusEnabled();
  const onSaved = useAutosaveAnalytics();
  // Send flow: viewers/commenters never save. A ref, because the listeners
  // below bind once, before the loaded document says who the caller is.
  const { canEdit } = useDocPermissions();
  const canEditRef = useRef(canEdit);
  useLayoutEffect(() => {
    canEditRef.current = canEdit;
  }, [canEdit]);

  const updatePreviewAndMeta = useUpdatePreviewAndMeta();
  const updatePreviewOnly = useUpdatePreviewOnly();
  // "Collaborative" here means the Yjs/Hocuspocus webhook owns the durable
  // contentPointer, so autosave only refreshes the preview (updatePreviewOnly).
  // Without Hocuspocus enabled, there is no Y.Doc flush, so even collaborative
  // (broadcast) accounts must persist contentPointer themselves via the REST
  // path (updatePreviewAndMeta) — gate on Hocuspocus actually running.
  const usesYjsPersistence = isCollaborative && hocuspocusEnabled;
  const isCollaborativeRef = useRef(usesYjsPersistence);
  const versionRef = useRef(version);
  // The content to save is read through refs too, so a save triggered from an
  // event listener reads what is on screen NOW. The force-save listener below
  // is registered once (its effect depends on the persistence mode only), so
  // a `story`/`flatContentLayers` closed over by the render that registered it
  // stayed frozen at editor LOAD time. Share, Preview and Export dispatch
  // `force-save-story` whenever the preview is stale, and that uploaded the
  // load-time deck and promoted it over the head: the CAS gate passed because
  // the parent came from getLastKnownPointer, which WAS current. Everything
  // edited since open vanished on the next reload (Monday #13111578041,
  // Multivista: three reverts in one day, one permanent).
  const storyRef = useRef(story);
  const flatContentLayersRef = useRef(flatContentLayers);
  // Sync the refs after render, not during it (React render-purity rule).
  // This effect MUST stay declared above the `changeId` effect below: effects
  // run in declaration order within a commit, and that effect calls
  // handleSave in the same commit that changed the content.
  useEffect(() => {
    isCollaborativeRef.current = usesYjsPersistence;
    versionRef.current = version;
    storyRef.current = story;
    flatContentLayersRef.current = flatContentLayers;
  }, [usesYjsPersistence, version, story, flatContentLayers]);

  /**
   * Keeps the version's own name in step with the editor's, writing nothing
   * else: the durable content is the webhook's to own on this path, and
   * `data` is the one field this endpoint takes on its own.
   */
  const syncVersionTitle = useCallback(
    async (version: IVersion | null | undefined, story: StoryType) => {
      const title = story?.settings?.title;
      if (!version || typeof title !== "string" || !title.trim()) return;
      if (title === version.data?.title) return;
      // THROWS ON FAILURE, deliberately. This used to swallow the error and
      // let the caller report a successful save: the rename lived in the
      // content blob alone while the lists, the share dialog and the link all
      // kept the old name, and nothing on screen said so. The caller reports
      // it the same way it reports any failed save.
      return await updateVersionInDatabase({
        versionId: version._id,
        payload: { data: { ...version.data, title } },
      }).unwrap();
    },
    [updateVersionInDatabase],
  );

  const saveStory = useCallback(
    debounce(async (skipAnalytics?: boolean) => {
      // Read the content when the debounce FIRES, not when the save was
      // queued: up to 3 s of local edits and any co-editor edits (which do
      // not bump changeId) land in between, and the upload is tagged with
      // the store's hash at fire time, so the two must describe the same
      // content.
      const rest = storyRef.current;
      const flatContent = flatContentLayersRef.current;
      if (!rest) {
        dispatch(setIsSaving(false));
        return;
      }
      const version = versionRef.current;
      // The document this save is FOR. By the time the network returns, the
      // store may hold a different one (template <-> version navigation
      // remounts the editor and the loader swaps the document underneath a
      // save in flight). Mirroring this save's result into that document
      // re-installed the version over the template, and every following save
      // went into the version's lineage. Persist the save; only the Redux
      // bookkeeping is conditional.
      const scope = { storyId: rest._id, versionId: version?._id ?? null };
      const isStillLoaded = () =>
        isLoadedDocument(reduxStore.getState(), scope);
      const slides = invertFlatData({ flatData: flatContent });
      const storyWithContent = {
        ...rest,
        slides,
        layoutVersion: window.BUNDLE_VERSION,
      };

      if (isCollaborativeRef.current) {
        // GUARDED LIKE THE PATH BELOW. Neither await here was: a preview
        // upload that failed left `isSaving` true for the rest of the
        // session -- the spinner never stops and the editor looks stuck --
        // and a rename that failed was reported as a save that worked.
        try {
          // Collab mode: webhook owns contentPointer. Only refresh the FE-
          // rendered preview HTML + previewUrl so the Preview button opens a
          // page that matches what's on screen.
          await updatePreviewOnly({
            story: storyWithContent as never,
            versionId: version?._id,
            // Compile the preview with the version's merge-tag data so the
            // shared link shows resolved values, not raw `{{variable}}` (matches
            // the non-collab updatePreviewAndMeta path below).
            versionData: version?.data,
          });
          // A VERSION IS NAMED ON ITS RECORD, not in its content. The title
          // is edited through story.settings, and the non-collab path below
          // copies it onto the version on every save -- this path returned
          // without ever doing so, so a rename lived in the content blob
          // alone: the lists, the share dialog and the link (which the
          // backend titles from version.data.title) all kept the old name.
          // A deck built by AI meets this every time, since it opens under
          // its document type and is renamed to its own title once the
          // outline is read.
          const renamed = await syncVersionTitle(version, storyWithContent);
          if (renamed && isStillLoaded()) dispatch(setVersion(renamed));
        } catch (error) {
          trackContent("error edit save", {
            "failure message":
              error && typeof error === "object" && "message" in error
                ? error.message
                : "",
          });
          toast.error("An error occurred while saving your story");
        } finally {
          dispatch(setIsSaving(false));
        }
        return;
      }

      const story = await updatePreviewAndMeta({
        story: storyWithContent,
        skipUpdate: !!version,
        versionId: version?._id,
        skipHistoryUpdate: skipAnalytics,
        versionData: version?.data,
      });

      try {
        if (!version) {
          if (isStillLoaded())
            dispatch(
              updateStoryByKeys({
                data: {
                  layoutVersion: window.BUNDLE_VERSION,
                  previewUrl: story.previewUrl,
                  updatedAt: story.updatedAt,
                  lastSessionId: story.lastSessionId,
                  lastUpdateUserEmail: story.lastUpdateUserEmail,
                  settings: { contentPointer: story.settings.contentPointer },
                },
              }),
            );
          if (!skipAnalytics) onSaved();
        } else {
          const versionPayload = {
            ...version,
            data: { ...version.data, title: story.settings.title },
            previewUrl: story.previewUrl,
            contentPointer: story.settings.contentPointer,
            history: story.history,
            slides: story.slides,
          };
          const updatedVersion = await updateVersionInDatabase({
            versionId: version?._id,
            payload: versionPayload,
          }).unwrap();

          // Keep the cached version in Redux in sync with the DB. Without
          // this, flows that read `state.story.version` (e.g. duplicate
          // version) keep using a stale contentPointer and end up creating
          // duplicates from a pre-edit blob — dropping recently-added
          // elements like the AcceptButton from the duplicated version.
          if (isStillLoaded()) {
            dispatch(setVersion(updatedVersion));

            dispatch(
              updateStoryByKeys({
                data: {
                  layoutVersion: window.BUNDLE_VERSION,
                  previewUrl: story.previewUrl,
                  updatedAt: updatedVersion.updatedAt,
                  lastSessionId: story.lastSessionId,
                  lastUpdateUserEmail: story.lastUpdateUserEmail,
                  settings: { contentPointer: story.settings.contentPointer },
                },
              }),
            );
          }
        }
      } catch (error) {
        trackContent("error edit save", {
          "failure message":
            error && typeof error === "object" && "message" in error
              ? error.message
              : "",
        });
        toast.error("An error occurred while saving your story");
      }
      dispatch(setIsSaving(false));
    }, 3000),
    [],
  );

  const handleSave = ({ skipAnalytics }: { skipAnalytics?: boolean }) => {
    if (!canEditRef.current) return;
    dispatch(setIsSaving(true));
    saveStory(skipAnalytics);
  };

  useEffect(() => {
    // In collab mode the debounce still fires, but saveStory routes through
    // updatePreviewOnly — it refreshes the preview HTML + previewUrl without
    // racing the Yjs webhook on contentPointer. The original full autosave
    // runs as before for single-seat accounts.
    if (
      changeId &&
      changeId !== lastChangeId.current &&
      isAutosave &&
      !me?.skipAutosave
    ) {
      lastChangeId.current = changeId;
      handleSave({});
    }
    // usesYjsPersistence (not just isCollaborative) so a cold-cache Hocuspocus
    // flag resolving true re-runs this with the correct persistence branch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeId, isCollaborative, usesYjsPersistence]);

  useEffect(() => {
    handleForceSave(handleSave.bind(null, { skipAnalytics: true }));
    const handleAutosaveToggle = (e: Event) => {
      const details = (e as CustomEvent).detail;
      dispatch(handleAutosave(details));
    };
    // Stable reference for add/remove (binding inline produced two different
    // functions, so the old removeEventListener silently never detached).
    const handleForceSaveEvent = () => {
      handleSave({ skipAnalytics: false });
      // Run the debounced save NOW so on-demand regeneration (the stale
      // preview/publish fallback in the header triggers) doesn't wait out the
      // 3s autosave debounce.
      saveStory.flush();
    };
    document.addEventListener("toggle-auto-save", handleAutosaveToggle);
    document.addEventListener("force-save-story", handleForceSaveEvent);
    return () => {
      document.removeEventListener("toggle-auto-save", handleAutosaveToggle);
      document.removeEventListener("force-save-story", handleForceSaveEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollaborative, usesYjsPersistence]);
};

const handleForceSave = debounce((callback: () => void) => {
  const url = new URL(window.location.href);
  const autosave = url.searchParams.get("autosave");
  if (!autosave) return;
  callback();
  url.searchParams.delete("autosave");
  window.history.replaceState({}, document.title, url.toString());
}, 500);
